// Контроль веса участника.
//
// Вес пишется в FIT заезда (activities.fit -> athlete.weight, кг). Бэкенд отдает историю
// веса по соревновательным заездам участника — поле rider_weights в результатах события
// (см. EventResultsService#rider_weights): один замер на гонку, по возрастанию даты,
// включая гонки других событий.
//
// Глубину задает бэкенд: все заезды с начала турнира плюс пять до него (baseline) —
// вес, с которым участник пришел на турнир. Сравниваем вес на последней гонке
// с самым тяжелым замером этой истории: если он снизился на WEIGHT_DROP_THRESHOLD_KG
// и больше — участник отмечается в личном зачете.

import { localeFor } from './timeFormat';

// Порог фиксации снижения, кг. Мелкие колебания весов не отмечаем
export const WEIGHT_DROP_THRESHOLD_KG = 0.5;

const toTime = (value) => {
  if (!value) return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
};

// Граница отбора заездов — конец переданного среза этапов. Заезды позже нее не учитываем,
// чтобы протокол на промежуточный этап не подсвечивал вес из более поздних гонок.
// Берем максимум из времени старта последнего этапа среза и времени заездов на нем:
// заезд может состояться позже старта этапа
export const getWeightCutoffTime = (routeDetails) => {
  const stages = routeDetails || [];
  const lastStage = stages[stages.length - 1];
  if (!lastStage) return null;

  const times = [toTime(lastStage.route ? lastStage.route.start_date_time_utc : null)];
  (lastStage.results || []).forEach(r => times.push(toTime(r.activity_start_date)));

  const known = times.filter(time => time !== null);
  return known.length > 0 ? Math.max(...known) : null;
};

// Замеры веса участника по соревновательным заездам, не позже границы среза
export const collectRiderWeights = (userId, riderWeights, cutoffTime) => {
  const history = (riderWeights && riderWeights[userId]) || [];

  return history.reduce((measurements, ride) => {
    const weight = ride.weight != null ? parseFloat(ride.weight) : NaN;
    const rideTime = toTime(ride.date);
    const withinCutoff = cutoffTime == null || (rideTime !== null && rideTime <= cutoffTime);

    if (!Number.isNaN(weight) && weight > 0 && withinCutoff) {
      measurements.push({
        weight,
        date: ride.date,
        eventName: ride.event_name || null,
        routeName: ride.route_name || null,
        // Заезд до старта турнира — база, с которой участник в него зашел
        baseline: !!ride.baseline
      });
    }

    return measurements;
  }, []);
};

// Замеры веса участника для графика: вся история контроля, не позже границы среза
export const getRiderWeightSeries = (userId, riderWeights, routeDetails) => {
  return collectRiderWeights(userId, riderWeights, getWeightCutoffTime(routeDetails));
};

// Снижение веса на последней гонке относительно остальных заездов истории.
// Возвращает null, если снижения нет или сравнивать не с чем.
// current - последняя гонка, reference - самый тяжелый из предыдущих замеров
export const getRiderWeightDrop = (userId, riderWeights, routeDetails) => {
  const window = getRiderWeightSeries(userId, riderWeights, routeDetails);
  if (window.length < 2) return null;

  const current = window[window.length - 1];
  const previous = window.slice(0, -1);
  const reference = previous.reduce((heaviest, item) => (
    item.weight > heaviest.weight ? item : heaviest
  ), previous[0]);

  const drop = Math.round((reference.weight - current.weight) * 10) / 10;
  if (drop < WEIGHT_DROP_THRESHOLD_KG) return null;

  return { drop, current, reference, measurements: window };
};

// Сколько заездов показываем в подсказке: история за турнир бывает длинной,
// в значке нужны только последние замеры плюс тот, от которого считалось снижение
const TITLE_MEASUREMENTS = 6;

// Подсказка для значка: величина снижения и вес по последним учтенным заездам
export const formatWeightDropTitle = (weightDrop, t, language) => {
  if (!weightDrop) return '';

  const header = t('results.weight_alert')
    .replace('{drop}', formatWeight(weightDrop.drop));

  const tail = weightDrop.measurements.slice(-TITLE_MEASUREMENTS);
  const shown = tail.includes(weightDrop.reference) ? tail : [weightDrop.reference, ...tail.slice(1)];

  const lines = shown.map(item => {
    const name = item.eventName || item.routeName;
    const key = name ? 'results.weight_alert_ride' : 'results.weight_alert_ride_plain';

    return t(key)
      .replace('{date}', formatWeightDate(item.date, language))
      .replace('{weight}', formatWeight(item.weight))
      .replace('{name}', name || '');
  });

  return [header, ...lines].join('\n');
};

// Вес и дата заезда в одном виде для значка и графика
export const formatWeight = (value) => {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

export const formatWeightDate = (value, language) => {
  const time = toTime(value);
  if (time === null) return '';

  return new Date(time).toLocaleDateString(localeFor(language, 'en-GB'), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};
